/**
 * ChannelGridList — rendert die Channel-Sektionen des Produktbilder-Sub-Tabs
 * gestapelt untereinander (M1). Aus ProductImagesSubTab extrahiert, um das
 * Orchestrator-File zu entlasten und die Liste isoliert testbar zu halten.
 *
 * Loading/Empty/Stapel-Rendering; Drag&Drop/Pending/Save bleiben über die
 * durchgereichten Hook-Objekte unverändert.
 */

import { useTranslation } from 'react-i18next';
import type { useQuery } from '@tanstack/react-query';
import { Images, CloudDownload } from 'lucide-react';
import type { ProductAsset } from '@/types/products';
import { Loading, EmptyState, Button as UiButton } from '@/components/v2/ui';
import { DroppableChannelGrid } from './DroppableChannelGrid';
import type { usePendingOperations } from './usePendingOperations';
import type { useImageChanges } from './useImageChanges';
import type { useChannelOrders } from './useChannelOrders';
import type { useImageDragDrop } from './useImageDragDrop';
import type { useUploadHandler } from './useUploadHandler';
import type { ImageSize } from './types';

export interface ChannelGridListProps {
  assetsQuery: ReturnType<typeof useQuery>;
  assets: ProductAsset[];
  sortedChannelKeys: string[];
  getOrderedAssets: (key: string) => ProductAsset[];
  orders: ReturnType<typeof useChannelOrders>;
  changes: ReturnType<typeof useImageChanges>;
  pending: ReturnType<typeof usePendingOperations>;
  dnd: ReturnType<typeof useImageDragDrop>;
  productId: string;
  imageSize: ImageSize;
  channelMeta: Record<string, { channel_name: string; channel_display_name: string; channel_type: string | null; channel_id_jtl: string }>;
  optimizingChannel: string | null;
  handleOpenGallery: (id: string, name: string) => void;
  handleUploadSuccess: ReturnType<typeof useUploadHandler>;
  handleOptimizeImages: (channelId?: string) => void;
  handleViewSettings: (id?: string, name?: string) => void;
  handleViewHistory: (id: string, name: string) => void;
  handleViewRequirements: (id: string, name: string) => void;
  setLightboxImage: (a: ProductAsset) => void;
  handleSyncAll: () => void;
  syncing: boolean;
  onDeleteSlot?: (channelId: string, linkId: string, slot: number, isCascade?: boolean) => void;
  isPendingDelete?: (channelId: string, linkId: string) => boolean;
  isCascadeDelete?: (channelId: string, linkId: string) => boolean;
  onUndoDelete?: (channelId: string, linkId: string) => void;
  getReplacement?: (channelId: string, oldLinkId: string) => { newAssetId: string } | undefined;
}

/** Renders loading, empty, or the stacked channel grid list. */
export function ChannelGridList(props: ChannelGridListProps) {
  const { t } = useTranslation();
  const { assetsQuery, assets, sortedChannelKeys, getOrderedAssets, orders, changes, pending, dnd, productId, imageSize } = props;

  if (assetsQuery.isLoading) {
    return <div className="flex items-center justify-center py-12"><Loading size="lg" label={t('common.loading')} /></div>;
  }

  if (assets.length === 0 && sortedChannelKeys.length === 0) {
    return (
      <EmptyState icon={<Images className="h-12 w-12" />} title="Keine Bilder" description="Synchronisiere Produktbilder vom Provider."
        action={<UiButton variant="primary" onClick={props.handleSyncAll} disabled={props.syncing}><CloudDownload className="h-4 w-4 mr-2" />Bilder synchronisieren</UiButton>}
      />
    );
  }

  return (
    <div className="space-y-8 flex-1 overflow-y-auto">
      {sortedChannelKeys.map((channelKey) => {
        const orderedAssets = getOrderedAssets(channelKey);
        const isGlobal = channelKey === 'global';
        const meta = props.channelMeta[channelKey];
        const channelName = isGlobal
          ? t('products.allChannels')
          : meta?.channel_name || orderedAssets[0]?.channel_name || channelKey;
        const globalMeta = props.channelMeta['global'] as { channel_uuid?: string } | undefined;
        const channelId = isGlobal ? (globalMeta?.channel_uuid || null) : channelKey;
        const orderState = orders.channelOrders.get(channelKey);
        const channelHasChanges = (orderState && JSON.stringify(orderState.originalOrder) !== JSON.stringify(orderState.currentOrder)) || changes.changedChannelKeys.has(channelKey);

        return (
          <DroppableChannelGrid key={channelKey} channelKey={channelKey} channelName={channelName} isGlobal={isGlobal} assets={orderedAssets} hasChanges={!!channelHasChanges}
            onImageClick={props.setLightboxImage} onInitialize={() => orders.initializeChannelOrder(channelKey)} productId={productId} channelId={channelId}
            onOpenGallery={props.handleOpenGallery} onUploadSuccess={props.handleUploadSuccess} onOptimizeImages={props.handleOptimizeImages}
            onViewSettings={props.handleViewSettings} onViewHistory={props.handleViewHistory} onViewRequirements={props.handleViewRequirements}
            optimizingChannel={props.optimizingChannel} imageSize={imageSize} activeId={dnd.activeId}
            isDropTarget={dnd.overChannelKey === channelKey && dnd.activeDragData?.channelKey !== channelKey}
            changedAssetIds={changes.changedAssetIds} pendingUploadAssetIds={pending.pendingUploadAssetIds}
            onDeleteSlot={isGlobal ? (chId, linkId, slot) => props.onDeleteSlot?.(chId, linkId, slot, true) : props.onDeleteSlot}
            isPendingDelete={props.isPendingDelete}
            isCascadeDelete={isGlobal ? props.isCascadeDelete : undefined}
            onUndoDelete={props.onUndoDelete}
            getReplacement={props.getReplacement}
          />
        );
      })}
    </div>
  );
}
